import { useCallback, useMemo, useRef, useState } from "react";
import {
  ID_DOCUMENT_VERIFICATION_RETRIABLE_HTTP_STATUS_CODES,
  ID_DOCUMENT_VERIFICATION_STATUSES,
} from "../../../config";
import { useActionCallback } from "../../../../../../utils/hooks/useActionCallback";

/**
 * Shared verify orchestration for ID document verification.
 * Intentionally UI-agnostic: callers decide when to request verify and how to advance.
 */
export const useIdDocumentVerificationVerifyAdvance = ({
  dispatch,
  onboardingDetailsId,
  idDocumentVerification,
  onVerifyDone,
}) => {
  const pendingVerifyAdvanceRef = useRef(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const verificationStatus = useMemo(() => {
    return (
      idDocumentVerification?.lastVerifyStatus ??
      idDocumentVerification?.status ??
      ID_DOCUMENT_VERIFICATION_STATUSES.NULL
    );
  }, [
    idDocumentVerification?.lastVerifyStatus,
    idDocumentVerification?.status,
  ]);

  const finishVerify = useCallback(() => {
    if (!pendingVerifyAdvanceRef.current) return;
    pendingVerifyAdvanceRef.current = false;
    setIsVerifying(false);
    onVerifyDone?.(verificationStatus);
  }, [onVerifyDone, verificationStatus]);

  useActionCallback(
    "REQUEST_POST_ID_DOCUMENT_VERIFICATION_VERIFY_FULFILLED",
    finishVerify
  );
  useActionCallback(
    "REQUEST_POST_ID_DOCUMENT_VERIFICATION_VERIFY_REJECTED",
    (actions) => {
      const lastAction = actions?.[actions.length - 1];
      const errorStatus =
        lastAction?.requestStatus ?? lastAction?.payload?.status ?? null;
      const isRetriableVerifyError =
        ID_DOCUMENT_VERIFICATION_RETRIABLE_HTTP_STATUS_CODES.includes(
          errorStatus
        );

      if (isRetriableVerifyError) {
        pendingVerifyAdvanceRef.current = false;
        setIsVerifying(false);
        return;
      }

      finishVerify();
    }
  );

  const requestVerify = useCallback(() => {
    pendingVerifyAdvanceRef.current = true;
    setIsVerifying(true);
    dispatch({
      type: "REQUEST_POST_ID_DOCUMENT_VERIFICATION_VERIFY",
      payload: { onboarding_details_id: onboardingDetailsId },
    });
  }, [dispatch, onboardingDetailsId]);

  const cancelVerify = useCallback(() => {
    pendingVerifyAdvanceRef.current = false;
    setIsVerifying(false);
  }, []);

  return {
    isVerifying,
    verificationStatus,
    pendingVerifyAdvanceRef,
    requestVerify,
    cancelVerify,
    finishVerify,
  };
};
